/**
 * Le metriche dei PDF per GA4: pagine, caratteri, densita'.
 *
 * PERCHE' NON RIAPRE IL FILE
 *
 * Prima `analyzePdf` rileggeva il PDF intero (`file.arrayBuffer()`) e apriva
 * pdf.js sul thread principale, in parallelo al worker che lo stava gia'
 * leggendo per davvero: due letture dello stesso file, di cui una bloccava la
 * pagina, solo per mandare tre numeri a GA4. Ora quei numeri li produce il
 * worker (`done` porta pagine, caratteri e pagine senza testo) e qui si fa
 * solo da tramite: chi estrae li PUBBLICA, chi traccia li ASPETTA. Gli eventi
 * tengono gli stessi nomi e gli stessi campi di prima.
 */

// Le metriche gia' note, per file. Chiave debole: se il File viene raccolto,
// sparisce anche la voce.
const metriche = new WeakMap();
// Chi sta aspettando le metriche di un file che non e' ancora stato letto.
const inAttesa = new WeakMap();

// Un caricamento grande puo' durare minuti; oltre questo tempo si smette di
// aspettare e si traccia con gli zeri, che a GA4 vale "non misurato".
const ATTESA_MASSIMA_MS = 2 * 60 * 1000;

const METRICHE_VUOTE = { page_count: 0, text_length: 0, text_density: 0 };

/**
 * Chi legge il PDF (il worker, via textExtractor) passa di qui i suoi conti.
 * @param {File} file
 * @param {{pagine:number, caratteri:number, pagineSenzaTesto?:number}} conti
 */
export const pubblicaMetrichePdf = (file, conti) => {
  if (!file || !conti) return;
  const pagine = Number(conti.pagine) || 0;
  const caratteri = Number(conti.caratteri) || 0;
  const valori = {
    page_count: pagine,
    text_length: caratteri,
    text_density: pagine > 0 ? Math.round(caratteri / pagine) : 0,
    pages_without_text: Number(conti.pagineSenzaTesto) || 0,
  };
  metriche.set(file, valori);
  const svegli = inAttesa.get(file);
  if (svegli) {
    inAttesa.delete(file);
    svegli.forEach((risolvi) => risolvi(valori));
  }
};

/**
 * Le metriche del PDF, quando la lettura le produce.
 *
 * Non apre il file: aspetta che `pubblicaMetrichePdf` le porti. Se nessuno
 * leggera' mai quel file (limite documenti raggiunto, caricamento annullato)
 * torna gli zeri dopo `attesaMs`, o subito con `attesaMs: 0`.
 *
 * @param {File} file
 * @param {{attesaMs?: number}} [opzioni]
 * @returns {Promise<{page_count:number, text_length:number, text_density:number}>}
 */
export const analyzePdf = (file, { attesaMs = ATTESA_MASSIMA_MS } = {}) => {
  if (!file) return Promise.resolve({ ...METRICHE_VUOTE, error: 'file mancante' });
  const note = metriche.get(file);
  if (note) return Promise.resolve(note);
  if (attesaMs <= 0) return Promise.resolve({ ...METRICHE_VUOTE, error: 'non letto' });

  return new Promise((risolvi) => {
    const timer = setTimeout(() => {
      const coda = inAttesa.get(file);
      if (coda) inAttesa.set(file, coda.filter((r) => r !== sveglia));
      risolvi({ ...METRICHE_VUOTE, error: 'attesa scaduta' });
    }, attesaMs);
    const sveglia = (valori) => {
      clearTimeout(timer);
      risolvi(valori);
    };
    inAttesa.set(file, [...(inAttesa.get(file) || []), sveglia]);
  });
};

/**
 * Verifica se un file è un PDF
 * @param {File} file - File da verificare
 * @returns {boolean} - true se è un PDF
 */
export const isPdfFile = (file) => {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
};

/**
 * Ottiene informazioni base del file per analytics
 * @param {File} file - File da analizzare
 * @returns {Object} - Informazioni base del file
 */
export const getFileInfo = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
  return {
    file_type: extension,
    mime_type: file.type || 'unknown',
    file_size_bytes: file.size,
    file_size_mb: parseFloat((file.size / 1024 / 1024).toFixed(2))
  };
};

const pdfAnalytics = {
  analyzePdf,
  pubblicaMetrichePdf,
  isPdfFile,
  getFileInfo
};

export default pdfAnalytics;
